import { useEffect, useState, type CSSProperties, type ReactNode } from "react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { getSalesStats, type SalesStats } from "../../../lib/sales";
import { Card, PageHead, StatusPill, fmtMoney, fmtNum } from "../../ui";

/** One figure from the sales KPI strip. */
function Stat({ label, value, sub, tone }: { label: string; value: string; sub?: ReactNode; tone?: "wt" | "lt" }) {
  return (
    <div className="crm-q">
      {/* The cell is a flex box, so the figure's column has to be allowed to
          shrink. Without `min-w-0` its auto minimum is the widest unbreakable
          thing inside it - a formatted sum, or the overdue pill - and on a
          phone the cell then refuses to fit the column it was given. */}
      <span className="min-w-0">
        <span className="crm-q-lab block">{label}</span>
        <span className={cn("crm-q-val block", tone === "wt" && "text-amber-600", tone === "lt" && "text-red-600")}>
          {value}
        </span>
        {sub ? <span className="crm-prow-s block">{sub}</span> : null}
      </span>
    </div>
  );
}

/** The frame every sales sub-page sits in: the section heading, the shared KPI
 *  strip and the sub-page's own title. Moving between the document kinds is the
 *  sidebar's job (see SALES_CHILDREN in crm/nav.ts). */
export function SalesShell({ titleKey, introKey, children }: { titleKey: string; introKey?: string; children: ReactNode }) {
  const t = useT();
  const [stats, setStats] = useState<SalesStats | null>(null);

  useEffect(() => {
    getSalesStats().then(setStats).catch(() => {});
  }, []);

  return (
    <>
      <PageHead title={t("sales.title")} sub={introKey ? t(introKey) : t("sales.intro")} />

      {stats && (
        <Card
          className="crm-quick"
          // Two columns on a phone rather than the default one: five figures
          // stacked one to a row push the list a whole screen down, and these
          // five are short enough to sit two abreast. See `--crm-quick-cols-xs`.
          style={{ "--crm-quick-cols": 5, "--crm-quick-cols-xs": 2 } as CSSProperties}
        >
          <Stat
            label={t("sales.stats.revenueMonth")}
            value={fmtMoney(stats.revenueMonthCents)}
            sub={t("sales.stats.yearSub", { v: fmtMoney(stats.revenueYearCents) })}
          />
          <Stat
            label={t("sales.stats.outstanding")}
            value={fmtMoney(stats.outstandingCents)}
            tone="wt"
            sub={t("sales.stats.invoicesN", { n: stats.outstandingCount })}
          />
          <Stat
            label={t("sales.stats.overdue")}
            value={fmtMoney(stats.overdueCents)}
            tone="lt"
            sub={
              stats.overdueCount ? (
                <StatusPill tone="lt">{t("sales.stats.invoicesN", { n: stats.overdueCount })}</StatusPill>
              ) : (
                t("sales.stats.invoicesN", { n: 0 })
              )
            }
          />
          <Stat label={t("sales.stats.openEstimates")} value={fmtNum(stats.openEstimateCount)} />
          <Stat label={t("sales.stats.draftInvoices")} value={fmtNum(stats.draftInvoiceCount)} />
        </Card>
      )}

      {/* The sub-page names itself: the head above says "Sales", and without this
          a list of credit notes and a list of invoices look identical. */}
      <h2 className="crm-card-t mb-3 mt-1 px-1.5">{t(titleKey)}</h2>

      {children}
    </>
  );
}
